import React, { useState, useEffect } from 'react';
import {
    Button,
    MenuItem,
    withStyles,
    makeStyles,
    Grid,
    Theme,
} from '@material-ui/core';
import { DefaultRootState, useDispatch, useSelector } from 'react-redux';
import { AddEditTeamLead } from '../../store/actions/cmActionCreator';
import Modal from './Modal';
import { CustomTextField } from '../Textfield';
import { getHubList } from '../../store/actions/staffActionCreator';
import { enqueueSnackbar } from '../../store/actions/notificationActionCreator';

interface FormState {
    name: string;
    contactNumber: string;
    city: any;
    hub: any;
    id?: string;
}

interface InputStatus {
    error: boolean;
    message?: string;
}

interface FormStatus {
    name?: InputStatus;
    contactNumber?: InputStatus;
    city?: InputStatus;
    hub?: InputStatus;
    [key: string]: InputStatus | undefined;
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
        listStyle: 'none',
        padding: theme.spacing(0.5),
        margin: 0,
        marginTop: '12px',
        marginBottom: '24px',
    },
    chip: {
        margin: theme.spacing(0.5),
        '&$deleteIconOutlinedColorPrimary': {
            color: 'red',
        },
    },
}));

const StyledMenuItem = withStyles((theme: Theme) => ({
    root: {
        margin: '0 8px',
        border: '0 !important',
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: 'rgba(67, 65, 158, 0.08)',
            color: '#43419e',
            borderRadius: '8px',
        },
    },
}))(MenuItem);

interface AddTeamLeadModalProps {
    open: boolean;
    onClose: () => void;
    isEdit?: boolean;
    data?: Partial<FormState>;
    ref?: React.Ref<any>;
}

const preProceedPayload = (data: any) => {
    const payload = {
        name: data?.name,
        contactNumber: data?.contactNumber,
        cityId: data?.city?.id ?? data?.city,
        hubId: data?.hub?.id ?? data?.hub,
        orgId: data?.orgId,
    };
    return payload;
};

const AddTeamLeadModal: React.FC<AddTeamLeadModalProps> = ({
    open,
    onClose,
    isEdit = false,
    data = {},
}) => {
    const { user, cm, staff, settings } = useSelector(
        (store: DefaultRootState | any) => store || {},
    );
    const cityList = settings?.cities ?? [];
    const orgId = settings?.basicDetails?.id;
    const hubList = staff?.hubs ?? [];

    const dispatch = useDispatch();

    const dispatchEditStaffTeamLead = (
            data: any,
            onSuccess: (resp: any) => void,
            onError: (err: any) => void,
        ) => dispatch(AddEditTeamLead(data, onSuccess, onError)),
        showMessage = (msg: string, type: string) =>
            dispatch(
                enqueueSnackbar({
                    message: msg,
                    options: {
                        key: new Date().getTime() + Math.random(),
                        variant: type,
                    },
                }),
            ),
        dispatchGetHubList = (onSuccess?: Function, onError?: Function) =>
            dispatch(getHubList(orgId, onSuccess, onError));

    const classes = useStyles();
    const [isOpen, setIsOpen] = useState(open);
    const [isLoading, setIsLoading] = useState(false);

    const [state, setState] = useState<any>({
        name: '',
        contactNumber: '',
        city: '',
        hub: '',
    });

    const [stateStatus, setStateStatus] = useState<FormStatus>({});

    const handleClose = () => {
        setIsOpen(false);
        onClose?.();
    };

    useEffect(() => {
        dispatchGetHubList(
            () => {},
            () => {},
        );
        if (data?.id) {
            setState({
                ...state,
                ...data,
            });
        }
    }, []);

    const validateForm = (): boolean => {
        let isValid = true;
        const newStatus: FormStatus = {};

        // Validate name
        if (!state.name || state.name.trim() === '') {
            newStatus.name = {
                error: true,
                message: 'Name is required',
            };
            isValid = false;
        } else if (state.name.length < 2) {
            newStatus.name = {
                error: true,
                message: 'Name must be at least 2 characters',
            };
            isValid = false;
        }

        // Validate contact number
        const phoneRegex = /^[0-9]{10,10}$/;
        if (!state.contactNumber) {
            newStatus.contactNumber = {
                error: true,
                message: 'Contact number is required',
            };
            isValid = false;
        } else if (!phoneRegex.test(state.contactNumber)) {
            newStatus.contactNumber = {
                error: true,
                message: 'Please enter a valid contact number (10 digits)',
            };
            isValid = false;
        }

        // Validate city
        if (!state.city) {
            newStatus.city = {
                error: true,
                message: 'City is required',
            };
            isValid = false;
        }

        // Validate hub
        if (!state.hub) {
            newStatus.hub = {
                error: true,
                message: 'Hub is required',
            };
            isValid = false;
        }

        setStateStatus(newStatus);
        return isValid;
    };

    const actions = [
        <Button
            key={1}
            variant="contained"
            color="default"
            className="cancelButton"
            onClick={handleClose}
            disableElevation
        >
            Cancel
        </Button>,
        <Button
            key={2}
            variant="contained"
            color="primary"
            className="saveButton"
            disabled={isLoading}
            onClick={async () => {
                if (!validateForm()) {
                    return;
                }

                setIsLoading(true);
                const payload = {
                    ...state,
                    orgId,
                };

                dispatchEditStaffTeamLead(
                    preProceedPayload(payload),
                    async () => {
                        console.log(
                            `${isEdit ? 'Edit' : 'Add'} Team Lead SUCCESS`,
                        );
                        showMessage(
                            `${isEdit ? 'Edit' : 'Add'} Team Lead SUCCESS`,
                            'success',
                        );
                        setIsLoading(false);
                        handleClose();
                    },
                    async (err: any) => {
                        setIsLoading(false);
                        console.log(
                            `${isEdit ? 'Edit' : 'Add'} Team Lead FAILED`,
                            err,
                        );
                    },
                );
            }}
            disableElevation
        >
            {isEdit ? 'Save' : 'Add'}
        </Button>,
    ];

    const handleInputChange = (value: string, key: string) => {
        setState((prev: any) => ({
            ...prev,
            [key]: value,
        }));

        // Clear validation error when user starts typing
        if (stateStatus?.[key]) {
            setStateStatus((prev) => ({
                ...prev,
                [key]: { error: false, message: '' },
            }));
        }
    };

    return (
        <Modal
            onClose={handleClose}
            open={isOpen}
            title={`${isEdit ? 'Edit' : 'Add'} Team Lead`}
            actions={actions}
            loading={isLoading}
            subtitle={undefined}
            maxWidth={undefined}
            className={undefined}
            fullScreen={undefined}
            isForm={undefined}
            onSubmit={undefined}
            subtitleStyle={undefined}
        >
            <Grid container spacing={2} style={{ marginTop: '16px' }}>
                <Grid item xs={12}>
                    <CustomTextField
                        fullWidth={true}
                        label="Full Name"
                        placeholder="Enter Full Name"
                        value={state?.name}
                        onChange={(e: any) =>
                            handleInputChange(e.target.value, 'name')
                        }
                        error={stateStatus?.name?.error}
                        helperText={stateStatus?.name?.message}
                        isRequired={true}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CustomTextField
                        fullWidth={true}
                        label="Contact Number"
                        placeholder="Enter Contact Number"
                        value={state?.contactNumber}
                        onChange={(e: any) =>
                            handleInputChange(e.target.value, 'contactNumber')
                        }
                        error={stateStatus?.contactNumber?.error}
                        helperText={stateStatus?.contactNumber?.message}
                        isRequired={true}
                    />
                </Grid>
                <Grid item xs={6}>
                    <CustomTextField
                        label="City"
                        select
                        style={{ marginBottom: '16px' }}
                        id="cityId"
                        name="cityId"
                        value={state?.city}
                        onChange={(e: any) =>
                            handleInputChange(e.target.value, 'city')
                        }
                        error={stateStatus?.city?.error}
                        helperText={stateStatus?.city?.message}
                        isRequired={true}
                    >
                        {cityList?.length === 0 && (
                            <MenuItem value="">No Data found</MenuItem>
                        )}
                        {cityList?.map((city: any) => (
                            <MenuItem key={city.id} value={city.id}>
                                {city.name}
                            </MenuItem>
                        ))}
                    </CustomTextField>
                </Grid>
                <Grid item xs={6}>
                    <CustomTextField
                        label="Hub"
                        select
                        style={{ marginBottom: '16px' }}
                        id="hubId"
                        name="hubId"
                        value={state?.hub}
                        onChange={(e: any) =>
                            handleInputChange(e.target.value, 'hub')
                        }
                        error={stateStatus?.hub?.error}
                        helperText={stateStatus?.hub?.message}
                        isRequired={true}
                    >
                        {hubList?.length === 0 && (
                            <MenuItem value="">No Data found</MenuItem>
                        )}
                        {hubList?.map((hub: any) => (
                            <MenuItem key={hub.id} value={hub.id}>
                                {hub.name}
                            </MenuItem>
                        ))}
                    </CustomTextField>
                </Grid>
            </Grid>
        </Modal>
    );
};

export default AddTeamLeadModal;
